/* eslint-disable no-console */
/* eslint-disable @typescript-eslint/no-floating-promises */
import {Carousel, Image} from 'antd';
import React, {useEffect, useMemo, useRef, useState} from 'react';
import {Document, pdfjs} from 'react-pdf';

import Loader from '../../components/Loader/Loader';

import ImageWithPolygonOverlay from './components/ImageWithPolygonOverlay';
import {NormalizedPolygon} from './utils/polygonUtils';

const SLIDE_INNER_STYLE: React.CSSProperties = {
  width: '100%',
  height: '100%'
};

const PdfRender: React.FC<{
  pdfUrl: string;
  handleImageLoad: (ref: any) => void;
  setPage: React.Dispatch<React.SetStateAction<number>>;
  setImgLoaded: React.Dispatch<React.SetStateAction<boolean>>;
  polygons?: NormalizedPolygon[];
  carouselHeight?: number | string;
}> = ({
  pdfUrl,
  handleImageLoad,
  setPage,
  setImgLoaded,
  polygons = [],
  carouselHeight = '100%'
}) => {
  const [numPages, setNumPages] = useState<number | null>(null);
  const [pageImages, setPageImages] = useState<string[]>([]);
  const canvasRefs = useRef<Array<HTMLCanvasElement | null>>([]);

  pdfjs.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.mjs';

  const onDocumentLoadSuccess = ({
    numPages: totalPages
  }: {
    numPages: number;
  }) => {
    setNumPages(totalPages);
  };

  useEffect(() => {
    setPageImages([]);
    canvasRefs.current = [];
  }, [pdfUrl]);

  const renderPageToImage = async (
    pdfPage: any,
    canvas: HTMLCanvasElement | null,
    pageNumber: number
  ) => {
    const context = canvas?.getContext('2d');
    const viewport = pdfPage.getViewport({scale: 2});
    if (context && canvas) {
      canvas.width = viewport.width;
      canvas.height = viewport.height;

      const renderContext = {
        canvasContext: context,
        viewport
      };

      await pdfPage.render(renderContext).promise;

      const image = canvas.toDataURL('image/png');
      setPageImages((prevImages) => {
        const newImages = [...prevImages];
        newImages[pageNumber - 1] = image;

        return newImages;
      });
    }
  };

  useEffect(() => {
    if (numPages) {
      (async () => {
        const pdf = await pdfjs.getDocument(pdfUrl).promise;
        for (let i = 1; i <= numPages; i++) {
          const canvas = canvasRefs.current[i - 1];
          const page = await pdf.getPage(i);
          await renderPageToImage(page, canvas, i);
        }
      })();
    }
  }, [numPages, pdfUrl]);

  const slideMinHeight =
    typeof carouselHeight === 'number' ? carouselHeight : 400;

  const carouselStyle = useMemo(
    (): React.CSSProperties => ({
      width: '100%',
      height: carouselHeight
    }),
    [carouselHeight]
  );

  const slideStyle = useMemo(
    (): React.CSSProperties => ({
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: '100%',
      height: carouselHeight,
      minHeight: slideMinHeight
    }),
    [carouselHeight, slideMinHeight]
  );

  const pageImageStyle: React.CSSProperties = {
    width: '100%',
    height: '100%',
    objectFit: 'contain'
  };

  const outerWrapperStyle: React.CSSProperties = {
    width: '100%',
    height: '100%'
  };

  return (
    <div style={outerWrapperStyle}>
      <Document
        key={pdfUrl}
        file={pdfUrl}
        onLoadSuccess={onDocumentLoadSuccess}
        loading={<Loader />}
      >
        <Image.PreviewGroup>
          <Carousel
            rootClassName="pdf-document-carousel"
            arrows
            infinite={false}
            style={carouselStyle}
            afterChange={(curr) => {
              setPage(curr + 1);
              setImgLoaded(false);
            }}
          >
            {Array.from(new Array(numPages), (_el, index) => (
              <div key={`page_${index + 1}`} style={slideStyle}>
                <canvas
                  ref={(el) => {
                    canvasRefs.current[index] = el;
                  }}
                  style={{display: 'none'}}
                />

                {pageImages[index] ? (
                  <div style={SLIDE_INNER_STYLE}>
                    <ImageWithPolygonOverlay
                      src={pageImages[index]}
                      alt={`Page ${index + 1}`}
                      style={pageImageStyle}
                      polygons={polygons}
                      page={index + 1}
                      fillContainer
                      onLoad={handleImageLoad}
                    />
                  </div>
                ) : (
                  <Loader />
                )}
              </div>
            ))}
          </Carousel>
        </Image.PreviewGroup>
      </Document>
    </div>
  );
};

export default PdfRender;
